/**
 * Global Omnis confirmation/alert – use this instead of native alert() or confirm().
 * Renders a single custom modal; any component can call useConfirmation().alert() / .confirm().
 */

import { createContext, useContext, useState, useCallback, ReactNode } from 'react'
import { ConfirmationModal } from '@components/UserManagement/ConfirmationModal'

export interface ConfirmOptions {
  title?: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: 'warning' | 'danger' | 'info'
}

export interface AlertOptions {
  title?: string
  message: string
  buttonLabel?: string
  variant?: 'warning' | 'danger' | 'info'
}

interface ConfirmationContextValue {
  confirm: (messageOrOptions: string | ConfirmOptions, title?: string) => Promise<boolean>
  alert: (messageOrOptions: string | AlertOptions, title?: string) => Promise<void>
  /** No-op when using ConfirmationProvider; dialog is rendered globally. */
  ConfirmDialog: () => null
}

const ConfirmationContext = createContext<ConfirmationContextValue | null>(null)

export function ConfirmationProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [config, setConfig] = useState<ConfirmOptions & { isAlertMode: boolean }>({
    message: '',
    title: 'Confirm',
    confirmLabel: 'OK',
    cancelLabel: 'Cancel',
    variant: 'warning',
    isAlertMode: false
  })
  const [resolver, setResolver] = useState<((value: boolean) => void) | null>(null)

  const confirm = useCallback((messageOrOptions: string | ConfirmOptions, title?: string): Promise<boolean> => {
    return new Promise((resolve) => {
      const options: ConfirmOptions = typeof messageOrOptions === 'string'
        ? { message: messageOrOptions, title: title || 'Confirm' }
        : messageOrOptions
      setConfig({
        message: options.message,
        title: options.title || 'Confirm',
        confirmLabel: options.confirmLabel || 'OK',
        cancelLabel: options.cancelLabel || 'Cancel',
        variant: options.variant || 'warning',
        isAlertMode: false
      })
      setIsOpen(true)
      setResolver(() => (value: boolean) => resolve(value))
    })
  }, [])

  const alert = useCallback((messageOrOptions: string | AlertOptions, title?: string): Promise<void> => {
    return new Promise((resolve) => {
      const options: AlertOptions = typeof messageOrOptions === 'string'
        ? { message: messageOrOptions, title: title || 'Alert' }
        : messageOrOptions
      setConfig({
        message: options.message,
        title: options.title || 'Alert',
        confirmLabel: options.buttonLabel || 'OK',
        cancelLabel: '',
        variant: options.variant || 'info',
        isAlertMode: true
      })
      setIsOpen(true)
      setResolver(() => () => resolve())
    })
  }, [])

  const handleConfirm = useCallback(() => {
    if (resolver) {
      resolver(true)
      setResolver(null)
    }
    setIsOpen(false)
  }, [resolver])

  const handleCancel = useCallback(() => {
    if (resolver) {
      resolver(false)
      setResolver(null)
    }
    setIsOpen(false)
  }, [resolver])

  const ConfirmDialog = () => null
  return (
    <ConfirmationContext.Provider value={{ confirm, alert, ConfirmDialog }}>
      {children}
      <ConfirmationModal
        isOpen={isOpen}
        title={config.title || 'Confirm'}
        message={config.message}
        confirmLabel={config.confirmLabel || 'OK'}
        cancelLabel={config.isAlertMode ? undefined : (config.cancelLabel || 'Cancel')}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        variant={config.variant || 'warning'}
        isAlertMode={config.isAlertMode}
      />
    </ConfirmationContext.Provider>
  )
}

export function useConfirmation(): ConfirmationContextValue {
  const ctx = useContext(ConfirmationContext)
  if (!ctx) {
    throw new Error('useConfirmation must be used within ConfirmationProvider. Wrap your app with <ConfirmationProvider>.')
  }
  return ctx
}
